import { css, keyframes } from '@emotion/react'

type TJustify =
	| 'flex-start'
	| 'flex-end'
	| 'center'
	| 'space-between'
	| 'space-around'
	| 'space-evenly'
type TAlign = 'flex-start' | 'flex-end' | 'center' | 'baseline' | 'stretch'

interface IGetFlexboxProps {
	flow?: 'row' | 'column'
	wrap?: boolean
	align?: TAlign
	justify?: TJustify
}

const getFlexbox = ({
	flow = 'row',
	wrap = false,
	align = 'center',
	justify = 'center',
}: IGetFlexboxProps = {}) => `
    display: flex;
    flex-flow: ${flow} ${wrap ? 'wrap' : 'nowrap'};
    align-items: ${align};
    justify-content: ${justify};
  `

interface IGrowProps {
	animationDuration?: number
}

const grow = ({ animationDuration = 0.5 }: IGrowProps = {}) => {
	const frames = keyframes`
	from {
		transform: scale(0);
	}
	to {
		transform: scale(1);
	}`

	return css`
		animation: ${frames} ${animationDuration}s ease;
		transform-origin: center;
	`
}

interface IMinmaxProps {
	w?: string | null
	h?: string | null
}

const minmax = ({ w = '100%', h = '100%' }: IMinmaxProps = {}) => `
    min-width: ${w};
    min-height: ${h};
    max-width: ${w};
    max-height: ${h};
  `

interface IHoverTransitionProps {
	scale?: number
	transition?: string
	transitionTime?: number
}

const hoverTransition = ({
	scale = 0.95,
	transitionTime = 0.5,
	transition = 'transform',
}: IHoverTransitionProps = {}) => `
  transition: ${transition} ${transitionTime}s ease;
  &:hover {
    cursor: pointer;
    transform: scale(${scale});
  }
`

const hideScrollbar = `
  &::-webkit-scrollbar {
    width: 0px;
		height: 0px;
    background: transparent;
  }
  & {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`

const thumbColor = '#777777'

interface ICustomScrollbarOpts {
	padding?: number
	autoHide?: boolean
}

const yCustomScrollbar = ({
	padding = 0.5,
	autoHide = true,
}: ICustomScrollbarOpts = {}) => `
	padding-right: ${padding}rem;
	scroll-behavior: smooth;
	scrollbar-width: thin;
	scrollbar-color: transparent ${thumbColor};

  &::-webkit-scrollbar {
		height: 0;
		width: 4px;
    background: transparent;
  }
  & {
		-ms-overflow-style: none;
    scrollbar-width: none;
  }

	&::-webkit-scrollbar-track {
		background: transparent;
	}

	&::-webkit-scrollbar-thumb {
		visibility: ${autoHide ? 'hidden' : 'visible'};
		border-radius: 10px;
		background: ${thumbColor};

	}

	&:hover {
		&::-webkit-scrollbar-thumb {
			visibility: visible;
		}
	}
	`

const xCustomScrollbar = ({ padding = 0.25 }: ICustomScrollbarOpts = {}) => `
	padding-bottom: ${padding}rem;
	scroll-behavior: smooth;
	scrollbar-width: thin;
	scrollbar-color: transparent ${thumbColor};

  &::-webkit-scrollbar {
		height: 4px;
    background: transparent;
  }

	&::-webkit-scrollbar-corner {
		background-color: transparent;
	}

	&::-webkit-scrollbar-track {
    background: transparent;
	}

	&::-webkit-scrollbar-thumb {
		border-radius: 10px;
		background: ${thumbColor};
	}
`

interface IXYCustomScrollbarOpts {
	xPadding?: number
	yPadding?: number
}

const xyCustomScrollbar = ({
	xPadding,
	yPadding = 0,
}: IXYCustomScrollbarOpts = {}) => `
		${yCustomScrollbar({ padding: yPadding })}
		${xCustomScrollbar({ padding: xPadding })}
	`

const size = {
	mobileS: '320px',
	mobileM: '375px',
	mobileL: '425px',
	tablet: '768px',
	laptop: '1024px',
	laptopL: '1440px',
	desktop: '2560px',
}

const device = {
	mobileS: `(min-width: ${size.mobileS})`,
	mobileM: `(min-width: ${size.mobileM})`,
	mobileL: `(min-width: ${size.mobileL})`,
	tablet: `(min-width: ${size.tablet})`,
	laptop: `(min-width: ${size.laptop})`,
	laptopL: `(min-width: ${size.laptopL})`,
	desktop: `(min-width: ${size.desktop})`,
	desktopL: `(min-width: ${size.desktop})`,
}

export {
	device,
	getFlexbox,
	grow,
	hideScrollbar,
	hoverTransition,
	minmax,
	xCustomScrollbar,
	xyCustomScrollbar,
	yCustomScrollbar,
}
